import type {QueryParams} from '@wix/document-manager-utils'
import type {
    Callback1,
    ClientSpecMap,
    HttpErrorCallback,
    ParsedUrl,
    PS,
    RoutersDefinition,
    ValueOf
} from '@wix/document-services-types'
import _ from 'lodash'
import {warmupUtils, utils, platformInit} from '@wix/santa-ds-libs'
const {cookieUtils} = utils
const {ajaxLibrary} = warmupUtils
import pageData from '../../page/pageData'
import experiment from 'experiment-amd'
import {contextAdapter} from '../../utils/contextAdapter'
import {logWixCodeConsoleMessage, urlUtils} from '@wix/santa-core-utils'
import {compressString} from '@wix/routing-utils'
import constants from './constants'

const MEMBERS_APP_DEFINITION_ID = '14cc59bc-f0b7-15b8-e1c7-89ce41d0e0c9'

const SITE_STRUCTURE_SERVICE_ENDPOINT = '/_api/site-structure-server/v1/site-structure'

const REQUEST_TYPES = {
    PAGES: 'PAGES',
    SITE_MAP: 'SITE_MAP',
    SITE_MAP_COUNT: 'SITE_MAP_COUNT'
} as const

type RequestType = ValueOf<typeof REQUEST_TYPES>

const REQUEST_ROUTES = {
    PAGES: '/pages',
    SITE_MAP: '/sitemap',
    SITE_MAP_COUNT: '/sitemapCount'
} as const

const MAX_URL_LENGTH = 2048

enum HTTPMethod {
    GET = 'GET',
    POST = 'POST'
}

const buildGetRequestUrl = async (url: string, backendRequestObject): Promise<string> => {
    const urlParsed = urlUtils.parseUrl(url)
    const urlBase = new URL(urlParsed.full.replace(urlParsed.search, ''))
    const urlSearchParams = new URLSearchParams(urlParsed.query)

    const requestData = {
        urlParams: urlSearchParams.toString(),
        body: backendRequestObject
    }

    const compressedRequestData = await compressString(JSON.stringify(requestData))

    if (compressedRequestData) {
        return `${urlBase}?${compressedRequestData}`
    }

    return urlBase.toString()
}

function getRouterHttpRequestMethod(clientSpecMapEntry: Record<string, any>): HTTPMethod {
    return clientSpecMapEntry?.appFields?.platform?.routerHttpMethod || HTTPMethod.POST
}

function getRouterHttpRequestMethodByAppDefId(clientSpecMap: ClientSpecMap, appDefinitionId: string) {
    const appSpec = platformInit.specMapUtils.getAppSpec(clientSpecMap, appDefinitionId)
    return getRouterHttpRequestMethod(appSpec)
}

const getRoutersPathConfig = (instanceId: string) => ({
    'wix-code': `https://${instanceId}.dev.wix-code.com/routers/custom`,
    dataBinding: '/_api/dynamic-pages-router/v1'
})

function getWixCodeRouterPath({appDefinitionId, instanceId}: {appDefinitionId: string; instanceId: string}) {
    const resolvedRoutersPaths = getRoutersPathConfig(instanceId)
    return resolvedRoutersPaths[appDefinitionId]
}

function getRouterPath(currentUrl: ParsedUrl, clientSpecMap: ClientSpecMap, appDefinitionId: string) {
    const clientSpecMapForApplication = _.find(clientSpecMap, {appDefinitionId})
    // @ts-expect-error
    const routerPath = clientSpecMapForApplication.appFields.platform.routerServiceUrl
    if (appDefinitionId === MEMBERS_APP_DEFINITION_ID) {
        return routerPath
    }
    return routerPath
}

function getBackendRequestUrl(routerPath: string, currentUrl: ParsedUrl, requestType: RequestType) {
    const domainForRouter = currentUrl.host
    const requestRoute = REQUEST_ROUTES[requestType]
    if (routerPath.startsWith('/')) {
        return `${currentUrl.protocol}//${domainForRouter}${routerPath}${requestRoute}`
    }
    return `${routerPath}${requestRoute}`
}

function getUrlParams(instance: string) {
    const params = ['viewMode=editor']

    if (instance) {
        params.push(`instance=${instance}`)
    }

    return `?${params.join('&')}`
}

function getBackendRequestObject(
    pageRoles: ParamsObj['pageRoles'],
    suffix: string,
    routerDefinition: RoutersDefinition,
    publicBaseUrl: string,
    formFactor: string,
    queryParams: QueryParams
) {
    const routerConfig = _.isString(routerDefinition.config)
        ? JSON.parse(routerDefinition.config)
        : routerDefinition.config
    const routerSuffix = `/${suffix}`.replace(/^\/\//, '/')
    let fullUrl = `${publicBaseUrl}/${routerDefinition.prefix}${routerSuffix}`
    if (queryParams) {
        fullUrl = `${fullUrl}?${queryParams}`
    }
    //todo - temporarly send the /<prefix>/<suffix> in the fullUrl parameter, just until the router backend will be fixed to use the right parameters
    const result = {
        fullUrl,
        routerPrefix: `/${routerDefinition.prefix}`,
        routerSuffix,
        requestInfo: {
            formFactor
        },
        config: routerConfig,
        pageRoles
    }

    return result
}

function getCSRFTokenValue() {
    return cookieUtils.getCookie('XSRF-TOKEN')
}

function getWixCodeUrlParams(instance: string, gridAppId: string, appDefinitionId: string) {
    const viewMode = 'viewMode=editor'
    const _gridAppId = `gridAppId=${gridAppId}`
    const params = [viewMode, _gridAppId]

    if (instance && appDefinitionId !== 'dataBinding') {
        params.push(`instance=${instance}`)
    }

    return `?${params.join('&')}`
}

interface ParamsObj {
    queryParams?: QueryParams
    formFactor?: string
    publicBaseUrl: string
    suffix: string
    pageRoles: Record<
        string,
        {
            id: string
            title: string
        }
    >
    currentUrl: ParsedUrl
    clientSpecMap: ClientSpecMap
    routerDefinition: RoutersDefinition
    rendererModel_wixCodeModel_appData_codeAppId: string
}

function getInstance(type: HTTPMethod, instance: string) {
    return type === HTTPMethod.POST ? instance : undefined
}

function getWixCodeAjaxParameters({paramObj, requestType}: {paramObj: ParamsObj; requestType: RequestType}) {
    const {
        currentUrl,
        clientSpecMap,
        routerDefinition: {appDefinitionId},
        rendererModel_wixCodeModel_appData_codeAppId
    } = paramObj

    const appSpec = platformInit.specMapUtils.getAppSpec(
        clientSpecMap,
        experiment.isOpen('dm_cloudSiteExtensionMigrationDev') ? constants.VELO_APP_DEF_ID : undefined
    )
    const {instance, instanceId} = appSpec
    const routerPath = getWixCodeRouterPath({appDefinitionId, instanceId})
    const type = getRouterHttpRequestMethod(appSpec)
    const urlParams = getWixCodeUrlParams(
        getInstance(type, instance),
        rendererModel_wixCodeModel_appData_codeAppId,
        appDefinitionId
    )
    const url = getBackendRequestUrl(routerPath, currentUrl, requestType) + urlParams

    const successCallback = routerResponse => {
        if (_.get(routerResponse, 'result._elementoryError')) {
            logWixCodeConsoleMessage(`Internal server error:${routerResponse.result.stack}`)
        }
    }

    const extraAjaxParams = {
        xhrFields: {
            withCredentials: true
        },
        crossDomain: true
    }

    if (appDefinitionId === 'dataBinding') {
        _.assign(extraAjaxParams, {
            headers: {Authorization: experiment.isOpen('dm_ajaxAuthPhase2') ? undefined : instance},
            appIdAutoAuth: experiment.isOpen('dm_ajaxAuthPhase2') ? appDefinitionId : undefined
        })
    }

    if (type === HTTPMethod.GET) {
        _.assign(extraAjaxParams, {headers: {Authorization: instance}})
    }

    return {
        url,
        type,
        successCallback,
        extraAjaxParams
    }
}

function getAjaxParametersForRouterRequest({paramObj, requestType}: {paramObj: ParamsObj; requestType: RequestType}) {
    const {appDefinitionId} = paramObj.routerDefinition

    if (/(wix-code|dataBinding)/.test(appDefinitionId)) {
        return getWixCodeAjaxParameters({paramObj, requestType})
    }

    const {currentUrl, clientSpecMap} = paramObj
    const {instance} = platformInit.specMapUtils.getAppSpec(clientSpecMap, appDefinitionId)
    const type = getRouterHttpRequestMethodByAppDefId(clientSpecMap, appDefinitionId)
    const routerPath = getRouterPath(currentUrl, clientSpecMap, appDefinitionId)
    const urlParams = getUrlParams(getInstance(type, instance))
    const url = getBackendRequestUrl(routerPath, currentUrl, requestType) + urlParams

    const extraAjaxParams = type === HTTPMethod.GET ? {headers: {Authorization: instance}} : undefined

    return {
        url,
        type,
        extraAjaxParams
    }
}

async function getFromBackEnd(
    paramObj: ParamsObj,
    requestType: RequestType,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
): Promise<void> {
    const {url, type, successCallback, extraAjaxParams}: any = getAjaxParametersForRouterRequest({
        paramObj,
        requestType
    })

    const ajaxParams: warmupUtils.AjaxOp = {
        url,
        type,
        dataType: 'json',
        headers: {},
        success(routerResponse) {
            if (successCallback) {
                successCallback(routerResponse)
            }
            onSuccess(routerResponse)
        },
        error: onError
    }

    _.assign(ajaxParams, extraAjaxParams)
    _.assign(ajaxParams.headers, {'X-XSRF-TOKEN': getCSRFTokenValue()})

    const backendRequestObject = getBackendRequestObject(
        paramObj.pageRoles,
        paramObj.suffix,
        paramObj.routerDefinition,
        paramObj.publicBaseUrl,
        paramObj.formFactor,
        paramObj.queryParams
    )

    const setupGetRequest = async () => {
        try {
            const requestUrl = await buildGetRequestUrl(url, backendRequestObject)

            if (requestUrl.length > MAX_URL_LENGTH) {
                onError(`GET URL length exceeded the limit of ${MAX_URL_LENGTH} characters`)
            }
            ajaxParams.url = requestUrl
        } catch (e) {
            onError(`Failed to build GET request URL with error: ${e}`)
        }
    }

    const setupPostRequest = async () => {
        ajaxParams.data = backendRequestObject
    }

    const httpRequestSetupStrategy = {
        [HTTPMethod.GET]: setupGetRequest,
        [HTTPMethod.POST]: setupPostRequest
    }

    const prepareAjaxRequest = httpRequestSetupStrategy[type]
    await prepareAjaxRequest()

    ajaxLibrary.ajax(ajaxParams)
}

function makeParamObjFromPs(ps: PS, routerDefinition: RoutersDefinition): ParamsObj {
    const pages = _.map(pageData.getPagesList(ps, true, true), _pageId => ({
        pageId: _pageId,
        title: pageData.getPageData(ps, _pageId).title
    }))
    const paramObj: ParamsObj = {
        currentUrl: ps.siteAPI.getCurrentUrl(),
        clientSpecMap: ps.siteAPI.getClientSpecMap(),
        rendererModel_wixCodeModel_appData_codeAppId: ps.extensionAPI.wixCode.getEditedGridAppId(),
        routerDefinition,
        suffix: '',
        pageRoles: _.reduce(
            routerDefinition.pages,
            function (result, value, key) {
                result[key] = {
                    id: value,
                    title: _.find(pages, {pageId: value}).title
                }
                return result
            },
            {}
        ),
        publicBaseUrl: ps.siteAPI.getPublicBaseUrl()
    }
    return paramObj
}

function cleanPrefixesFromSiteMap(siteMapEntries, prefix: string) {
    const reg = new RegExp(`.*?\/${prefix}`)
    _.forEach(siteMapEntries, function (entry) {
        if (entry?.url) {
            entry.url = entry.url.replace(reg, '')
            if (entry.url.charAt(0) === '/' && entry.url.length > 1) {
                entry.url = entry.url.substring(1)
            }
        }
    })
}

function getInnerRoutesSiteMap(paramObj: ParamsObj, onSuccess: Callback1<any>, onError: Callback1<any>) {
    getInnerRoutesSiteMapByRequestType(paramObj, REQUEST_TYPES.SITE_MAP, onSuccess, onError)
}

function getInnerRoutesSiteMapCount(paramObj: ParamsObj, onSuccess: Callback1<any>, onError: Callback1<any>) {
    getInnerRoutesSiteMapByRequestType(paramObj, REQUEST_TYPES.SITE_MAP_COUNT, onSuccess, onError)
}

async function getInnerRoutesSiteMapByRequestType(
    paramObj: ParamsObj,
    requestType: RequestType,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
): Promise<void> {
    function onSiteMapResponse(routerResponse) {
        if (routerResponse.exception) {
            onError(routerResponse.result)
            return
        }
        const siteMap = routerResponse.result
        cleanPrefixesFromSiteMap(siteMap, paramObj.routerDefinition.prefix)
        onSuccess(routerResponse.result)
    }

    await getFromBackEnd(paramObj, requestType, onSiteMapResponse, onError)
}

/*
 * Expected request/result params can be viewed here:
 * https://github.com/wix-private/editor-server/blob/master/wix-site-structure/src/main/proto/com/wixpress/structure/api/site_structure_service.proto
 */
function getTpaRoutesFromSiteStructure(
    ps: PS,
    appDefinitionId: string,
    subPage: string,
    success: Callback1<any>,
    error: HttpErrorCallback
) {
    const clientSpecMap = ps.siteAPI.getClientSpecMap()
    const instance = _.get(platformInit.specMapUtils.getAppSpec(clientSpecMap, appDefinitionId), 'instance', '')
    const {protocol, host} = ps.siteAPI.getCurrentUrl()
    const url = `${protocol}//${host}${SITE_STRUCTURE_SERVICE_ENDPOINT}`
    const data: any = {
        pageVersion: 1,
        filter: {
            itemTypeIdentifier: subPage,
            sourceId: appDefinitionId,
            configId: ''
        }
    }
    const branchId = ps.extensionAPI.siteAPI.getBranchId()
    if (branchId) {
        data.branchId = branchId
    }
    contextAdapter.actions.sendHttpRequest(
        url,
        'POST',
        {
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            headers: {
                'X-XSRF-TOKEN': getCSRFTokenValue(),
                Authorization: experiment.isOpen('dm_ajaxAuthPhase2') ? undefined : instance
            },
            appIdAutoAuth: experiment.isOpen('dm_ajaxAuthPhase2') ? appDefinitionId : undefined,
            data
        },
        data,
        success,
        error
    )
}

export default {
    getInnerRoutesSiteMap,
    getInnerRoutesSiteMapCount,
    makeParamObjFromPs,
    getTpaRoutesFromSiteStructure
}
